"use client";

import { useState } from "react";
import Image from "next/image";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Clock,
  Headphones,
  MessageCircle,
  ChevronDown,
} from "lucide-react";

import {
  buildCdnUrl,
  CONTACT_DETAILS,
} from "@/shared/constants/publicUrlConstants";

import { Button } from "@/shared/components/ui/Button";
import { sendContactMessage } from "../services/contact.service";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    fullName: false,
    email: "",
    message: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      fullName: !formData.fullName.trim(),
      email: !formData.email.trim()
        ? "Please fill out this field."
        : !/^\S+@\S+\.\S+$/.test(formData.email)
        ? "Please enter a valid email address."
        : "",
      message: !formData.message.trim(),
    };

    setErrors(newErrors);

    if (newErrors.fullName || newErrors.email || newErrors.message) {
      return;
    }

    setIsSubmitting(true);
    try {
      await sendContactMessage({
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        subject: formData.subject || undefined,
        message: formData.message.trim(),
      });

      toast.success("Your message has been sent. Our team will get back to you within 24-48 business hours.");
      setFormData({ fullName: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      // error toast is handled by apiErrorHandler in the axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <div className="bg-white py-8 sm:py-12 text-center px-4">
        <h1 className="text-2xl sm:text-4xl font-bold text-[#001F5F] mb-3">
          Contact Us
        </h1>

        <p className="text-gray-500 max-w-lg mx-auto">
          Have questions or need assistance? We&apos;re here to help you every
          step of the way.
        </p>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="relative w-full h-[360px] sm:h-[420px] rounded-2xl overflow-hidden bg-[#001F5F]">
              <Image
                src={buildCdnUrl("/banners/contactus1.jpg")}
                alt="Support Team"
                fill
                className="object-cover"
                style={{ objectPosition: "center 30%" }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[#001F5F]/80 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  We&apos;re Here to Help
                </h2>

                <p className="text-white/80 text-sm">
                  Our dedicated team is ready to assist you with any questions
                  or concerns.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-[#001F5F] rounded-xl p-4 sm:p-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FACC15] rounded-lg flex items-center justify-center mb-8 sm:mb-16">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-[#001F5F]" />
                </div>

                <h3 className="font-bold text-white mb-1 text-sm sm:text-base">
                  Email Us
                </h3>

                <p className="text-xs sm:text-sm text-white/70 break-all">
                  {CONTACT_DETAILS.supportEmail}
                </p>
              </div>

              <div className="bg-[#001F5F] rounded-xl p-4 sm:p-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#FACC15] rounded-lg flex items-center justify-center mb-8 sm:mb-16">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-[#001F5F]" />
                </div>

                <h3 className="font-bold text-white mb-1 text-sm sm:text-base">
                  Call Us
                </h3>

                <p className="text-xs sm:text-sm text-white/70">
                  {CONTACT_DETAILS.supportPhoneDisplay}
                </p>
              </div>
            </div>

            <div className="relative h-[180px] sm:h-[220px] rounded-2xl overflow-hidden">
              <Image
                src={buildCdnUrl("/banners/contactus2.jpg")}
                alt="Quick Response"
                fill
                className="object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[#001F5F]/80 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-5 h-5 text-[#FACC15]" />

                  <h3 className="font-bold text-white">
                    Quick Response Time
                  </h3>
                </div>

                <p className="text-sm text-white/80">
                  We typically respond within 24-48 business hours. Your
                  satisfaction is our priority.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5 sm:p-7 flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#FFF7E0] rounded-lg flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-[#001F5F]" />
                </div>

                <div>
                  <h3 className="text-[#001F5F] text-sm sm:text-base mb-1">
                    Business Hours
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Mon-Fri: 9 AM - 7 PM
                    <br />
                    Sat: 10 AM - 5 PM
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-5 sm:p-7 flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#FFF7E0] rounded-lg flex items-center justify-center shrink-0">
                  <Headphones className="w-5 h-5 text-[#001F5F]" />
                </div>

                <div>
                  <h3 className="text-[#001F5F] text-sm sm:text-base mb-1">
                    24/7 Support
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                    Online support portal and
                    <br className="sm:hidden" />{" "}
                    live chat available.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-8 flex flex-col">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-2">
              Send Us a Message
            </h2>

            <p className="text-sm text-gray-500 mb-8">
              Fill out the form below and we&apos;ll get back to you soon.
            </p>

            <form className="space-y-6 flex-1 flex flex-col" onSubmit={handleSubmit} noValidate>
              <div>
                <label className="block text-sm font-medium text-[#001F5F] mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData({ ...formData, fullName: e.target.value });
                    if (errors.fullName) setErrors({ ...errors, fullName: false });
                  }}

                  className={`w-full rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FACC15] ${errors.fullName ? "border border-red-500" : "border border-gray-200"}`}
                />

                {errors.fullName && (
                  <p className="mt-1 text-xs text-red-500">
                    Please fill out this field.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#001F5F] mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>

                <input
  type="email"
  placeholder="Enter your email address"
  value={formData.email}
  onChange={(e) => {
    setFormData({ ...formData, email: e.target.value });
    if (errors.email) setErrors({ ...errors, email: "" });
  }}
  className={`w-full rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FACC15] ${errors.email ? "border border-red-500" : "border border-gray-200"}`}
/>

{errors.email && (
  <p className="mt-1 text-xs text-red-500">
    {errors.email}
  </p>
)}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#001F5F] mb-2">
                  Phone Number{" "}
                  <span className="text-gray-400">(Optional)</span>
                </label>

                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value,
                    })
                  }
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#001F5F] mb-2">
                  Subject <span className="text-gray-400">(Optional)</span>
                </label>

                <div className="relative">
                  <select
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subject: e.target.value,
                      })
                    }
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FACC15] appearance-none bg-white text-gray-400"
                  >
                    <option value="">Select a subject</option>
                    <option value="order">Order Inquiry</option>
                    <option value="product">Product Question</option>
                    <option value="returns">Returns & Refunds</option>
                    <option value="shipping">Shipping Information</option>
                    <option value="other">Other</option>
                  </select>

                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <label className="block text-sm font-medium text-[#001F5F] mb-2">
                  Message <span className="text-red-500">*</span>
                </label>

                <textarea
  rows={5}
  placeholder="Please provide details about your inquiry..."
  value={formData.message}
  onChange={(e) => {
    setFormData({ ...formData, message: e.target.value });
    if (errors.message) setErrors({ ...errors, message: false });
  }}
  className={`w-full rounded-lg px-4 py-3 text-sm resize-none flex-1 min-h-30 focus:outline-none focus:ring-2 focus:ring-[#FACC15] ${errors.message ? "border border-red-500" : "border border-gray-200"}`}
/>

{errors.message && (
  <p className="mt-1 text-xs text-red-500">
    Please fill out this field.
  </p>
)}
              </div>

              <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                <p className="text-xs text-gray-600">
                  <span className="font-medium text-[#001F5F]">
                    Note:
                  </span>{" "}
                  All fields marked with{" "}
                  <span className="text-red-500">*</span> are required.
                  We&apos;ll respond to your inquiry within 24-48 business
                  hours.
                </p>
              </div>

              <Button
                type="submit"
                variant="default"
                className="w-full py-4 rounded-lg font-bold text-sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}